import {
  ActivityIndicator,
  KeyboardAvoidingView,
  StyleSheet,
} from "react-native";
import { Heading, Paragraph, Text, View, YStack } from "tamagui";
import { Logo } from "../../components/Logo";
import { Button, Input } from "../../components/design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { SafeAreaView } from "react-native-safe-area-context";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { OnboardingNavigatorParamList } from "../../navigators/OnboardingNavigator";
import parsePhoneNumber, { AsYouType } from "libphonenumber-js";
import { customColorTokens } from "../../../tamagui.config";
import { WebView } from "react-native-webview";
import { Linking } from "react-native";

const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITEKEY ?? "";

function getTurnstileHtml(siteKey: string): string {
  const escapedKey = siteKey.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: transparent; }
    #turnstile-container { position: absolute; top: -9999px; left: -9999px; }
  </style>
  <script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" defer></script>
</head>
<body>
  <div id="turnstile-container"></div>
  <script>
    function sendToken(token) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'success', token: token }));
      }
    }
    function sendError(reason) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', reason: reason || 'unknown' }));
      }
    }
    window.addEventListener('load', function() {
      if (typeof turnstile === 'undefined') {
        sendError('script_failed');
        return;
      }
      try {
        turnstile.render('#turnstile-container', {
          sitekey: "${escapedKey}",
          theme: 'light',
          size: 'invisible',
          callback: sendToken,
          'error-callback': function() { sendError('widget_error'); },
          'expired-callback': function() { sendError('expired'); }
        });
      } catch (e) {
        sendError('render_failed');
      }
    });
  </script>
</body>
</html>
  `.trim();
}

export const Phone = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pendingFormattedPhoneRef = useRef<string | null>(null);
  const navigation =
    useNavigation<NavigationProp<OnboardingNavigatorParamList>>();

  const validatePhoneNumber = (number: string) => {
    const phoneNumber = parsePhoneNumber(number, "US");
    if (!phoneNumber) return false;
    return (
      phoneNumber.isValid() && ["US", "CA"].includes(phoneNumber.country ?? "")
    );
  };

  useEffect(() => {
    setIsPhoneValid(validatePhoneNumber(phoneNumber));
  }, [phoneNumber, setIsPhoneValid]);

  const handleCaptchaVerified = useCallback(
    async (token: string) => {
      console.log(
        "[Captcha] Verified, received token (length:",
        token?.length ?? 0,
        ")"
      );
      const formattedPhone = pendingFormattedPhoneRef.current;
      setShowCaptcha(false);
      setIsLoading(true);

      if (!formattedPhone) {
        console.warn("[Captcha] No pending phone number");
        setIsLoading(false);
        alert("Something went wrong. Please try again.");
        return;
      }

      console.log(
        "[Captcha] Sending OTP to",
        formattedPhone.replace(/\s/g, "")
      );
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone.replace(/\s/g, ""),
        options: { captchaToken: token },
      });

      pendingFormattedPhoneRef.current = null;

      setIsLoading(false);
      if (error) {
        console.error("[Captcha] signInWithOtp error:", error.message, error);
        alert(error.message);
      } else {
        console.log(
          "[Captcha] OTP sent successfully, navigating to Code screen"
        );
        navigation.navigate("Code", { phone: formattedPhone });
      }
    },
    [navigation]
  );

  const handleWebViewMessage = useCallback(
    (event: { nativeEvent: { data: string } }) => {
      try {
        const payload = JSON.parse(event.nativeEvent.data) as {
          type: string;
          token?: string;
          reason?: string;
        };
        if (payload.type === "success" && payload.token) {
          handleCaptchaVerified(payload.token);
        } else if (payload.type === "error") {
          setShowCaptcha(false);
          setIsLoading(false);
          const reason = payload.reason ?? "unknown";
          const domainHint =
            " For local testing, add localhost and about:blank to your Turnstile widget's allowed domains in the Cloudflare dashboard.";
          const msg =
            reason === "script_failed" || reason === "widget_error"
              ? `Verification failed (${reason}).${domainHint}`
              : "Verification failed. Please try again.";
          alert(msg);
        }
      } catch {
        setShowCaptcha(false);
        setIsLoading(false);
      }
    },
    [handleCaptchaVerified]
  );

  const sendCode = () => {
    const phone = parsePhoneNumber(phoneNumber, "US");
    const formattedPhone = phone?.formatInternational();

    if (formattedPhone) {
      pendingFormattedPhoneRef.current = formattedPhone;
      setIsLoading(true);
      setShowCaptcha(true);
    }
  };

  const asYouType = new AsYouType();

  return (
    <SafeAreaView
      style={{ flex: 1, backgroundColor: customColorTokens.brandParchment }}
    >
      <YStack
        justifyContent="space-between"
        flex={1}
        alignItems="flex-start"
        p="$4"
        w="100%"
        space={10}
      >
        <View w="100%" alignItems="center" justifyContent="center">
          <Logo />
          <Heading w="100%" size={"$2"} mt={32} textTransform="uppercase">
            Enter your phone number
          </Heading>
        </View>

        <KeyboardAvoidingView
          behavior="padding"
          enabled
          keyboardVerticalOffset={80}
          style={{ width: "100%", flex: 1 }}
        >
          <YStack space={4} w="100%" flex={1} justifyContent="space-between">
            <View justifyContent="center">
              <Input
                keyboardType="numeric"
                placeholder="Enter Phone"
                onChangeText={(text) => setPhoneNumber(text)}
                value={asYouType.input(phoneNumber)}
              />
              <Paragraph mt="$2" fontSize={12}>
                By entering your phone number and submitting this form, you
                agree to receive recurring automated text messages from
                Breakfast Club ATX at the number provided. Message frequency may
                vary. Message and data rates may apply. Text HELP for help or
                STOP to cancel at any time. See our{" "}
                <Text
                  fontSize={12}
                  textDecorationLine="underline"
                  onPress={() =>
                    Linking.openURL(
                      "https://breakfastclubatx.com/privacy-policy"
                    )
                  }
                >
                  Privacy Policy
                </Text>{" "}
                and{" "}
                <Text
                  fontSize={12}
                  textDecorationLine="underline"
                  onPress={() =>
                    Linking.openURL(
                      "https://breakfastclubatx.com/terms-of-service"
                    )
                  }
                >
                  Terms of Service
                </Text>{" "}
                for more details.
              </Paragraph>
            </View>
            <Button
              disabled={!isPhoneValid || isLoading}
              onPress={() => sendCode()}
              w="100%"
              opacity={isPhoneValid && !isLoading ? 1 : 0.5}
            >
              {isLoading ? <ActivityIndicator color="#fff" /> : "CONTINUE"}
            </Button>
          </YStack>
        </KeyboardAvoidingView>
      </YStack>

      {showCaptcha && (
        <WebView
          source={{
            html: getTurnstileHtml(TURNSTILE_SITE_KEY),
            baseUrl: "https://localhost/",
          }}
          style={styles.hiddenWebView}
          onMessage={handleWebViewMessage}
          javaScriptEnabled
          originWhitelist={["*"]}
          mixedContentMode="compatibility"
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  hiddenWebView: {
    position: "absolute",
    width: 0,
    height: 0,
    opacity: 0,
  },
});
